import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// Available themes
export const THEMES = {
  'royal-blue': {
    name: 'Royal Blue',
    description: 'Professional and corporate aesthetic',
    colors: ['#3b82f6', '#ffffff', '#f1f5f9']
  },
  'barbie-pink': {
    name: 'Barbie Pink',
    description: 'Light with pink essence',
    colors: ['#ec4899', '#fdf2f8', '#fbcfe8']
  },
  'caramel-indulgence': {
    name: 'Caramel Indulgence',
    description: 'Warm caramel tones',
    colors: ['#b45309', '#fef3c7', '#fde68a']
  },
  'ocean-depths': {
    name: 'Ocean Depths',
    description: 'Deep sea blues and teals',
    colors: ['#0d9488', '#f0fdfa', '#ccfbf1']
  },
  'regal-garden': {
    name: 'Regal Garden',
    description: 'Rich greens and purples',
    colors: ['#7c3aed', '#f5f3ff', '#ddd6fe']
  },
  'warm-neutrals': {
    name: 'Warm Neutrals',
    description: 'Soft and cozy earth tones',
    colors: ['#78716c', '#fafaf9', '#e7e5e4']
  }
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('gs-imaging-theme');
    // Validate saved theme exists in our theme list
    if (saved && THEMES[saved]) {
      return saved;
    }
    return 'royal-blue';
  });

  useEffect(() => {
    localStorage.setItem('gs-imaging-theme', theme);
    // Remove all theme classes and add current one
    const allThemes = Object.keys(THEMES);
    document.body.classList.remove(...allThemes);
    document.body.classList.add(theme);
  }, [theme]);

  const toggleTheme = () => {
    // Cycle through all themes
    const themeKeys = Object.keys(THEMES);
    const currentIndex = themeKeys.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themeKeys.length;
    setTheme(themeKeys[nextIndex]);
  };

  // Get next theme name for tooltip
  const getNextThemeName = () => {
    const themeKeys = Object.keys(THEMES);
    const currentIndex = themeKeys.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themeKeys.length;
    return THEMES[themeKeys[nextIndex]]?.name || 'Next Theme';
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    getNextThemeName,
    themes: THEMES,
    currentThemeName: THEMES[theme]?.name || 'Royal Blue',
    isRoyalBlue: theme === 'royal-blue',
    isBarbiePink: theme === 'barbie-pink',
    isCaramelIndulgence: theme === 'caramel-indulgence',
    isOceanDepths: theme === 'ocean-depths',
    isRegalGarden: theme === 'regal-garden',
    isWarmNeutrals: theme === 'warm-neutrals'
  };

  return (
    <ThemeContext.Provider value={value}>
      <div className={theme}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};
